import React, { JSX } from 'react';
import {
    Edit,
    TabbedForm,
    TextInput,
    required,
    NumberInput,
    BooleanInput,
    useRecordContext,
    TextField
} from 'react-admin';
import {
    Box
} from '@mui/material';
import { OperatorStatus } from "./components/Operator/OperatorStatus";
import { OperatorLogs } from "./components/Operator/OperatorLogs";
import { OperatorConnectionCount } from "./components/Operator/OperatorConnectionCount";
import { OperatorQueue } from "./components/Operator/OperatorQueue";
import { TokenItem } from "./components/Operator/TokenItem";

const EditOperatorTitle: () => JSX.Element = (): JSX.Element => {
    const record: any = useRecordContext();
    if (!record) return null;

    return <span>{ "Operator - " + record.name }</span>;
}

/**
 * The Operator edit screen, tabbed as of Stage 6 of the HQ queue screens.
 *
 * **It was a `SimpleForm` with three `<Grid>` panels stacked underneath it**, outside the form and
 * as siblings of it inside `<Edit>`. That worked, but it put the save toolbar in the middle of the
 * page with the status, connection count and log viewer scrolling on below it, and there was
 * nowhere to add the queue reading that did not make it worse. Tabs are the honest fix: the form is
 * one tab, each panel is its own, and the toolbar sits under whichever is open.
 *
 * **Every direct child of `TabbedForm` must be a `TabbedForm.Tab`.** `TabbedFormTabs` clones each
 * child into the tab bar, so anything else put here renders as a broken tab rather than as content.
 * The same note is on four other edit screens in this folder.
 *
 * The panels are unchanged and still talk to the Operator directly with a per-action ticket; only
 * where they sit has moved. No new ACL node: reaching this screen is `HQ_System::operators.view`
 * and the log viewer's own clear button already checks `.edit` for itself.
 *
 * **There is no `token` input.** The API never returns `Operator::$token` (HQAPI-76), so a required
 * input here always opened empty and blocked every save. The update is a merge PATCH, so leaving it
 * out keeps the stored value; it is set on the create screen only.
 */
export const OperatorEdit: () => JSX.Element = (): JSX.Element => {

    return (
        <Edit title={ <EditOperatorTitle /> }>
            <TabbedForm>
                <TabbedForm.Tab label="Details">
                    <TextInput name="name" source="name" validate={[required()]} fullWidth />
                    <TextInput name="host" source="host" validate={[required()]} fullWidth />
                    <NumberInput name="totalSlots" source="totalSlots" validate={[required()]} fullWidth />
                    <BooleanInput name="isSystem" source="isSystem" validate={[required()]} />
                    <TokenItem />
                    <Box>
                        <span style={ { marginRight: '10px' } }>{ "Available Slots: " }</span>
                        <TextField source="availableSlots" />
                    </Box>
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Status">
                    <OperatorStatus />
                    <OperatorConnectionCount />
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Queue">
                    <OperatorQueue />
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Logs">
                    <OperatorLogs />
                </TabbedForm.Tab>
            </TabbedForm>
        </Edit>
    );
}
